import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  companiesApi,
  type CompanyCustomerRow,
  type CompanyUsage,
  type CompanyUserRow,
} from '@/features/companies/companies.api';
import { type CreateUserData, type UpdateUserData, UserRole } from '@/types/user';
import { getErrorMessage } from '@/utils/errorHandling';
import { formatCNPJ } from '@/utils/cnpj';
import { formatBytes } from '@/utils/bytes';
import { useNotification } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { canImpersonate } from '@/constants/auth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EditCompanyDialog } from '@/components/EditCompanyDialog';
import { CreateCompanyUserDialog } from '@/components/CreateCompanyUserDialog';

function progressColor(percentage: number): 'success' | 'warning' | 'error' {
  if (percentage >= 90) return 'error';
  if (percentage >= 70) return 'warning';
  return 'success';
}

interface UsageBlockProps {
  label: string;
  used: number;
  limit: number;
  percentage: number;
  unit?: string;
  formatter?: (value: number) => string;
}

const UsageBlock: React.FC<UsageBlockProps> = ({
  label,
  used,
  limit,
  percentage,
  unit,
  formatter,
}) => {
  const fmt = (v: number) =>
    formatter ? formatter(v) : `${v.toLocaleString('pt-BR')}${unit ? ` ${unit}` : ''}`;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" fontWeight={700}>
          {percentage}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(percentage, 100)}
        color={progressColor(percentage)}
        sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
      />
      <Typography variant="caption" color="text.secondary">
        {fmt(used)} / {fmt(limit)}
      </Typography>
    </Box>
  );
};

type TabValue = 'overview' | 'users' | 'customers';

export const CompanyDetailPage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = params.id!;
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const showImpersonate = canImpersonate(user?.email);
  const [tab, setTab] = useState<TabValue>('overview');
  const [confirm, setConfirm] = useState<'activate' | 'deactivate' | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'companies', 'usage', companyId],
    queryFn: () => companiesApi.getUsage(companyId),
  });

  const deactivateMutation = useMutation({
    mutationFn: () => companiesApi.deactivate(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      showNotification('Empresa desativada com sucesso', 'success');
      setConfirm(null);
    },
    onError: err => showNotification(getErrorMessage(err, 'Erro ao desativar empresa'), 'error'),
  });

  const activateMutation = useMutation({
    mutationFn: () => companiesApi.activate(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      showNotification('Empresa reativada com sucesso', 'success');
      setConfirm(null);
    },
    onError: err => showNotification(getErrorMessage(err, 'Erro ao reativar empresa'), 'error'),
  });

  const impersonateMutation = useMutation({
    mutationFn: () => companiesApi.impersonate(companyId),
    onSuccess: ({ code, clientAppUrl }) => {
      const url = `${clientAppUrl.replace(/\/$/, '')}/impersonate?code=${encodeURIComponent(code)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    onError: err => {
      showNotification(getErrorMessage(err, 'Erro ao impersonar empresa'), 'error');
    },
  });

  return (
    <Box>
      <Button
        onClick={() => navigate(-1)}
        variant="text"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        Voltar
      </Button>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error, 'Erro ao carregar empresa')}
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {data && (
        <>
          <Header
            company={data}
            onEdit={() => setEditOpen(true)}
            onImpersonate={() => impersonateMutation.mutate()}
            onDeactivate={() => setConfirm('deactivate')}
            onActivate={() => setConfirm('activate')}
            showImpersonate={showImpersonate}
            isImpersonating={impersonateMutation.isPending}
          />

          <Paper sx={{ mt: 3 }}>
            <Tabs
              value={tab}
              onChange={(_, v: TabValue) => setTab(v)}
              sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
            >
              <Tab label="Visão geral" value="overview" />
              <Tab label="Clientes" value="customers" />
              <Tab label="Usuários" value="users" />
            </Tabs>

            <Box sx={{ p: 4 }}>
              {tab === 'overview' && <OverviewTab company={data} />}
              {tab === 'customers' && <CustomersTab companyId={companyId} />}
              {tab === 'users' && <UsersTab companyId={companyId} />}
            </Box>
          </Paper>

          <EditCompanyDialog open={editOpen} company={data} onClose={() => setEditOpen(false)} />
        </>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm === 'deactivate' ? 'Desativar empresa?' : 'Reativar empresa?'}
        message={
          confirm === 'deactivate' ? (
            <>
              Todos os usuários da empresa perderão acesso em até 15 minutos. Logins novos serão
              bloqueados imediatamente. Esta ação é reversível (reativação restaura acesso aos
              usuários ativos).
            </>
          ) : (
            <>
              Os usuários que estavam ativos antes da desativação voltarão a ter acesso. Usuários
              que foram individualmente desativados não serão afetados.
            </>
          )
        }
        confirmText={confirm === 'deactivate' ? 'Desativar' : 'Reativar'}
        severity={confirm === 'deactivate' ? 'error' : 'info'}
        onConfirm={async () => {
          if (confirm === 'deactivate') await deactivateMutation.mutateAsync();
          if (confirm === 'activate') await activateMutation.mutateAsync();
        }}
        onCancel={() => setConfirm(null)}
      />
    </Box>
  );
};

interface HeaderProps {
  company: CompanyUsage;
  onEdit: () => void;
  onImpersonate: () => void;
  onDeactivate: () => void;
  onActivate: () => void;
  showImpersonate: boolean;
  isImpersonating: boolean;
}

const Header: React.FC<HeaderProps> = ({
  company,
  onEdit,
  onImpersonate,
  onDeactivate,
  onActivate,
  showImpersonate,
  isImpersonating,
}) => {
  const isActive = !company.deletedAt;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={2}
    >
      <Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {company.legalName}
          </Typography>
          {isActive ? (
            <Chip label="Ativa" color="success" size="small" />
          ) : (
            <Chip label="Desativada" color="default" size="small" />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          CNPJ: {formatCNPJ(company.cnpj)}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={onEdit}>
          Editar
        </Button>
        {showImpersonate && isActive && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LoginIcon />}
            onClick={onImpersonate}
            disabled={isImpersonating}
          >
            Impersonar
          </Button>
        )}
        {isActive ? (
          <Button color="error" variant="outlined" startIcon={<BlockIcon />} onClick={onDeactivate}>
            Desativar
          </Button>
        ) : (
          <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={onActivate}>
            Reativar
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

const OverviewTab: React.FC<{ company: CompanyUsage }> = ({ company }) => {
  const isActive = !company.deletedAt;
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Indicadores de uso
      </Typography>

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 4 }}>
          <UsageBlock
            label="Clientes"
            used={company.customerCount}
            limit={company.customerLimit}
            percentage={company.customerPercentage}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <UsageBlock
            label="Tokens de IA"
            used={company.aiTokensUsed}
            limit={company.aiTokenLimit}
            percentage={company.aiPercentage}
            unit="tokens"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <UsageBlock
            label="Storage"
            used={company.storageUsed}
            limit={company.storageLimit}
            percentage={company.storagePercentage}
            formatter={formatBytes}
          />
        </Grid>
      </Grid>

      {company.accountMode === 'trial' && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Dados do cadastro
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Telefone / WhatsApp
              </Typography>
              <Typography>{company.adminPhone ?? 'Não informado'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Empresas gerenciadas
              </Typography>
              <Typography>
                {company.managedCompanies
                  ? `${company.managedCompanies} empresas`
                  : 'Não informado'}
              </Typography>
            </Grid>
          </Grid>
        </>
      )}

      {!isActive && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="text.secondary">
            Empresa desativada. Usuários perdem acesso em até 15 minutos após a desativação; logins
            novos são bloqueados imediatamente.
          </Typography>
        </>
      )}
    </Box>
  );
};

const CustomersTab: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'companies', companyId, 'customers'],
    queryFn: () => companiesApi.listCustomers(companyId),
  });

  const formatDate = (iso: string): string =>
    new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="h6">Clientes da empresa</Typography>
        {data && (
          <Typography variant="body2" color="text.secondary">
            ({data.length} {data.length === 1 ? 'cliente' : 'clientes'})
          </Typography>
        )}
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error, 'Erro ao carregar clientes')}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Razão social</TableCell>
                <TableCell>CNPJ</TableCell>
                <TableCell>Cadastrado em</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data || data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhum cliente cadastrado para esta empresa
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((c: CompanyCustomerRow) => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.legalName}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{formatCNPJ(c.cnpj)}</TableCell>
                    <TableCell>{formatDate(c.createdAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const UsersTab: React.FC<{ companyId: string }> = ({ companyId }) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<CompanyUserRow | null>(null);
  const [confirmUser, setConfirmUser] = useState<
    | { type: 'activate'; user: CompanyUserRow }
    | { type: 'deactivate'; user: CompanyUserRow }
    | { type: 'delete'; user: CompanyUserRow }
    | null
  >(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'companies', companyId, 'users'],
    queryFn: () => companiesApi.listUsers(companyId),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateUserData) =>
      companiesApi.createUser({
        ...input,
        role: input.role === UserRole.OPERATIONS ? 'operation' : 'admin',
        companyId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'users'] });
      showNotification('Usuário criado com sucesso', 'success');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: UpdateUserData }) => {
      const updateInput = {
        fullName: input.fullName,
        ...(input.role
          ? { role: input.role === UserRole.OPERATIONS ? ('operation' as const) : ('admin' as const) }
          : {}),
        ...(input.password ? { password: input.password } : {}),
      };

      return companiesApi.updateUser(userId, updateInput);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'users'] });
      showNotification('Usuário atualizado com sucesso', 'success');
    },
  });

  const activateMutation = useMutation({
    mutationFn: (userId: string) => companiesApi.activateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'users'] });
      showNotification('Usuário ativado com sucesso', 'success');
      setConfirmUser(null);
    },
    onError: err => showNotification(getErrorMessage(err, 'Erro ao ativar usuário'), 'error'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (userId: string) => companiesApi.deactivateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'users'] });
      showNotification('Usuário desativado com sucesso', 'success');
      setConfirmUser(null);
    },
    onError: err => showNotification(getErrorMessage(err, 'Erro ao desativar usuário'), 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => companiesApi.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'users'] });
      showNotification('Usuário deletado com sucesso', 'success');
      setConfirmUser(null);
    },
    onError: err => showNotification(getErrorMessage(err, 'Erro ao deletar usuário'), 'error'),
  });

  const handleCreateUser = async (input: CreateUserData | UpdateUserData) => {
    if (!input.email || !input.password || !input.fullName || !input.role) {
      throw new Error('Dados inválidos para criar usuário');
    }

    try {
      await createMutation.mutateAsync({
        email: input.email,
        password: input.password,
        fullName: input.fullName,
        role: input.role,
      });
    } catch (err) {
      throw new Error(getErrorMessage(err, 'Erro ao criar usuário'));
    }
  };

  const handleUpdateUser = async (input: CreateUserData | UpdateUserData) => {
    if (!editingUser) return;

    try {
      await updateMutation.mutateAsync({ userId: editingUser.id, input });
    } catch (err) {
      throw new Error(getErrorMessage(err, 'Erro ao atualizar usuário'));
    }
  };

  const handleConfirmUserAction = async () => {
    if (!confirmUser) return;

    if (confirmUser.type === 'activate') {
      await activateMutation.mutateAsync(confirmUser.user.id);
      return;
    }

    if (confirmUser.type === 'deactivate') {
      await deactivateMutation.mutateAsync(confirmUser.user.id);
      return;
    }

    await deleteMutation.mutateAsync(confirmUser.user.id);
  };

  const getRoleLabel = (role: CompanyUserRow['role']): string =>
    role === 'admin' ? 'Administrador' : 'Operação';

  const formatDate = (iso: string | null): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Usuários da empresa</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Novo usuário
        </Button>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error, 'Erro ao carregar usuários')}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Cargo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Último login</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data || data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhum usuário cadastrado para esta empresa
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((u: CompanyUserRow) => (
                  <TableRow key={u.id} hover>
                    <TableCell>{u.fullName}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Chip label={getRoleLabel(u.role)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {u.isActive ? (
                        <Chip label="Ativo" color="success" size="small" />
                      ) : (
                        <Chip label="Inativo" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{formatDate(u.lastLoginAt)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => setEditingUser(u)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={u.isActive ? 'Desativar' : 'Ativar'}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setConfirmUser({
                                type: u.isActive ? 'deactivate' : 'activate',
                                user: u,
                              })
                            }
                          >
                            {u.isActive ? (
                              <BlockIcon fontSize="small" />
                            ) : (
                              <CheckCircleIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmUser({ type: 'delete', user: u })}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateCompanyUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateUser}
      />
      <CreateCompanyUserDialog
        open={!!editingUser}
        mode="edit"
        user={editingUser ?? undefined}
        onClose={() => setEditingUser(null)}
        onSubmit={handleUpdateUser}
      />
      <ConfirmDialog
        open={!!confirmUser}
        title={
          confirmUser?.type === 'delete'
            ? 'Deletar Usuário'
            : confirmUser?.type === 'deactivate'
              ? 'Desativar Usuário'
              : 'Ativar Usuário'
        }
        message={
          confirmUser?.type === 'delete'
            ? `Tem certeza que deseja deletar o usuário "${confirmUser.user.fullName}"? Esta ação não pode ser desfeita.`
            : confirmUser?.type === 'deactivate'
              ? `Tem certeza que deseja desativar o usuário "${confirmUser.user.fullName}"? O usuário não poderá mais acessar o sistema.`
              : `Tem certeza que deseja ativar o usuário "${confirmUser?.user.fullName ?? ''}"? O usuário poderá acessar o sistema novamente.`
        }
        confirmText={
          confirmUser?.type === 'delete'
            ? 'Deletar'
            : confirmUser?.type === 'deactivate'
              ? 'Desativar'
              : 'Ativar'
        }
        severity={
          confirmUser?.type === 'delete'
            ? 'error'
            : confirmUser?.type === 'deactivate'
              ? 'warning'
              : 'info'
        }
        onConfirm={handleConfirmUserAction}
        onCancel={() => setConfirmUser(null)}
      />
    </Box>
  );
};
